import { z } from 'zod';
import { IsoTimestamp, SchoolId } from './common.js';
/**
 * Read-only AIMSIS academic mapping preview (AIMSIS-18806).
 *
 * Describes how academic reference and relationship data in the source AIMSIS
 * database maps onto the CBT master records that already exist in Firestore —
 * Grade Levels, Subjects, Classrooms, Class Memberships, and Teaching
 * Assignments. Nothing here is ever persisted: the response is a diagnosis an
 * administrator reads before deciding to run an apply step (AIMSIS-18807).
 */
/** The five academic data types the preview reports on, in display order. */
export const AcademicMappingEntityKind = z.enum([
    'gradeLevels',
    'subjects',
    'classrooms',
    'classMemberships',
    'teachingAssignments',
]);
/**
 * Per-entity tallies. `fetched` counts source rows; the remaining buckets
 * partition those rows by what the preview found in CBT. `unmapped` counts the
 * reverse direction — integrated CBT records with no matching source row in the
 * current window, which an apply step would archive.
 */
export const AcademicMappingCounts = z.object({
    fetched: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    new: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
    unmapped: z.number().int().nonnegative(),
    /** Sync-owned classrooms archived because an AIMSIS row claimed their name. */
    collided: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    validationErrors: z.number().int().nonnegative(),
});
export const AcademicMappingOutcome = z.enum([
    'matched',
    'new',
    'changed',
    'unmapped',
    /** A sync-owned classroom archived because an AIMSIS row used its name. */
    'collided',
    'conflict',
    'validationError',
]);
/**
 * One row of preview detail. Every field is optional-by-nature because a row
 * can be missing on either side: a source row with no CBT counterpart has no
 * `cbtId`, and an unmapped CBT record has no `externalId`.
 */
export const AcademicMappingRecord = z.object({
    /** External source identifier — AIMSIS primary key as a string. */
    externalId: z.string().nullable(),
    /** Human-readable label from the source, so an admin can recognise the row. */
    name: z.string().nullable(),
    /** Internal CBT document id, when a counterpart was found. */
    cbtId: z.string().nullable(),
    /** CBT record status (`active` / `inactive` / `archived`), when known. */
    cbtStatus: z.string().nullable(),
    /** Source-side status or liveness, when the source exposes one. */
    sourceStatus: z.string().nullable(),
    /** Relationship references (classroom, subject, teacher, effective dates). */
    references: z.record(z.string()).optional(),
    outcome: AcademicMappingOutcome,
    /** Actionable explanation for conflict / validationError rows. */
    message: z.string().nullable().optional(),
});
export const AcademicMappingEntity = z.object({
    kind: AcademicMappingEntityKind,
    /** True when the source of this data type could be read at all. */
    available: z.boolean(),
    counts: AcademicMappingCounts,
    records: z.array(AcademicMappingRecord),
    /** Per-entity reason when `available` is false. */
    unavailableReason: z.string().nullable().optional(),
});
/**
 * Where the preview read from. Mirrors the tenant metadata the Sync page
 * already shows, plus the optional academic context identifiers, which are
 * source metadata only — never CBT master records.
 */
export const AcademicMappingSourceContext = z.object({
    system: z.literal('aimsis'),
    tenant: SchoolId,
    academicDbName: z.string().min(1),
    academicSubdomain: z.string().nullable(),
    /** Optional academic context identifiers, displayed as source metadata. */
    academicContext: z
        .object({
        schoolYearId: z.number().int().nullable(),
        schoolYearName: z.string().nullable(),
        schoolPeriodId: z.number().int().nullable(),
        schoolPeriodName: z.string().nullable(),
    })
        .nullable(),
});
export const AcademicMappingPreview = z.object({
    /**
     * False when the tenant has no AIMSIS connection configured (standalone
     * mode). The page then shows an unavailable state and local CBT operation
     * continues unaffected.
     */
    configured: z.boolean(),
    /** Always false — this endpoint never writes. Stated in the payload itself. */
    written: z.literal(false),
    /**
     * True when a CBT collection read hit the preview's scan ceiling, so the
     * counts describe only the portion that was read. Surfaced so a partial read
     * is never presented as a complete figure.
     */
    truncated: z.boolean(),
    generatedAt: IsoTimestamp,
    source: AcademicMappingSourceContext.nullable(),
    entities: z.array(AcademicMappingEntity),
    warnings: z.array(z.string()),
});
//# sourceMappingURL=academic-mapping-preview.js.map